import { type UseFloatingReturn } from "@floating-ui/react-dom";
import { type Editor } from "@tiptap/core";
import TextAlign from "@tiptap/extension-text-align";
import { Divider, Space } from "antd";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import {
    BTIconTextAlignCenter,
    BTIconTextAlignLeft,
    BTIconTextAlignRight,
    BTIconTrash,
} from "commonComponents/btWrappers/BTIcon";
import {
    isAlignmentActive,
    isAlignmentEnabled,
} from "commonComponents/btWrappers/editor/core/editor.utils";
import { EditorControlButton } from "commonComponents/btWrappers/editor/core/EditorControlBar/common/EditorControlButton/EditorControlButton";

import "./ImageOptionsOverlay.less";

interface ILinkOptionsOverlayProps {
    editor: Editor;
    node: Editor["state"]["doc"];
    floatingManager: UseFloatingReturn;
}

export function ImageOptionsOverlay({ editor, floatingManager }: ILinkOptionsOverlayProps) {
    const isAlignEnabled = editor.extensionManager.extensions.some(
        (ext) => ext.name === TextAlign.name,
    );
    if (!isAlignEnabled) {
        return null;
    }
    const { x, y, refs, strategy } = floatingManager;

    return (
        <div
            ref={(ref) => refs.setFloating(ref)}
            className="ImageOptionsOverlay"
            style={{
                position: strategy,
                top: y ?? undefined,
                left: x ?? undefined,
            }}
            tabIndex={0}
            onMouseDown={(e) => {
                e.preventDefault(); // Prevent editor from losing focus when clicking on the overlay
            }}
        >
            <Space size={1} split={<Divider type="vertical" className="OptionDivider" />}>
                <Space size={2}>
                    <EditorControlButton
                        data-testid="leftAlignImage"
                        friendlyName="Left Align"
                        isToggled={isAlignmentActive(editor, "left")}
                        disabled={!isAlignmentEnabled(editor, "left")}
                        icon={<BTIconTextAlignLeft />}
                        onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    />
                    <EditorControlButton
                        data-testid="centerAlignImage"
                        friendlyName="Center Align"
                        icon={<BTIconTextAlignCenter />}
                        isToggled={isAlignmentActive(editor, "center")}
                        disabled={!isAlignmentEnabled(editor, "center")}
                        onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    />
                    <EditorControlButton
                        data-testid="rightAlignImage"
                        friendlyName="Right Align"
                        icon={<BTIconTextAlignRight />}
                        isToggled={isAlignmentActive(editor, "right")}
                        disabled={!isAlignmentEnabled(editor, "right")}
                        onClick={() => editor.chain().focus().setTextAlign("right").run()}
                    />
                </Space>
                <span>
                    <BTButtonDeprecated
                        id="deleteImage"
                        data-testid="deleteImage"
                        type="tertiary"
                        icon={<BTIconTrash />}
                        disabled={!editor.can().chain().focus().deleteSelection().run()}
                        onClick={() => editor.chain().focus().deleteSelection().run()}
                    />
                </span>
            </Space>
        </div>
    );
}
